import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Box, Paper } from '@mui/material'
import { createPortal } from 'react-dom'
import { TradingCostItem } from '../../proto/generated/order'
import ChargesBreakdownPanel from './ChargesBreakdownPanel'

const DRAWER_WIDTH = 320
/** Below quick-trade / place-order window (9999) so the panel sits behind it when closed. */
const DRAWER_Z_INDEX = 9998

type AnchorBounds = {
    top: number
    left: number
    height: number
}

const useAnchorBounds = (
    anchorRef: RefObject<HTMLElement | null>,
    trackPosition: boolean,
    anchorPositionKey?: unknown,
) => {
    const [bounds, setBounds] = useState<AnchorBounds | null>(null)

    const updateBounds = useCallback(() => {
        const anchor = anchorRef.current
        if (!anchor) {
            return
        }
        const rect = anchor.getBoundingClientRect()
        setBounds({
            top: rect.top,
            left: rect.left,
            height: rect.height,
        })
    }, [anchorRef])

    useLayoutEffect(() => {
        updateBounds()

        const anchor = anchorRef.current
        if (!anchor) {
            return
        }

        const observer = new ResizeObserver(updateBounds)
        observer.observe(anchor)
        window.addEventListener('resize', updateBounds)

        let frameId = 0
        if (trackPosition) {
            const tick = () => {
                updateBounds()
                frameId = window.requestAnimationFrame(tick)
            }
            frameId = window.requestAnimationFrame(tick)
        }

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', updateBounds)
            window.cancelAnimationFrame(frameId)
        }
    }, [trackPosition, updateBounds, anchorRef, anchorPositionKey])

    return bounds
}

type ChargesBreakdownDrawerProps = {
    open: boolean
    anchorRef: RefObject<HTMLElement | null>
    items: TradingCostItem[]
    total: number
    requiredMargin?: number
    availableMargin?: number
    brokerCode?: string
    /** Pass when the anchor moves (e.g. draggable window position) to refresh placement immediately. */
    anchorPositionKey?: unknown
    /** Skip close animation (e.g. when the parent place-order dialog is dismissed). */
    instantClose?: boolean
}

const ChargesBreakdownDrawer = ({
    open,
    anchorRef,
    items,
    total,
    requiredMargin,
    availableMargin,
    brokerCode,
    anchorPositionKey,
    instantClose = false,
}: ChargesBreakdownDrawerProps) => {
    const [mounted, setMounted] = useState(false)
    const [revealed, setRevealed] = useState(false)

    const trackPosition = open || mounted
    const bounds = useAnchorBounds(anchorRef, trackPosition, anchorPositionKey)

    const amountByCostType = useMemo(() => {
        const map = new Map<string, number>()
        for (const item of items ?? []) {
            map.set(item.costType, item.amount)
        }
        return map
    }, [items])

    const getAmount = useCallback(
        (costType: string) => amountByCostType.get(costType) ?? 0,
        [amountByCostType],
    )

    useEffect(() => {
        if (open) {
            setMounted(true)
            setRevealed(false)
            const frame = window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => setRevealed(true))
            })
            return () => window.cancelAnimationFrame(frame)
        }
        if (instantClose) {
            setRevealed(false)
            setMounted(false)
            return
        }
        setRevealed(false)
    }, [open, instantClose])

    const handleTransitionEnd = useCallback(
        (event: React.TransitionEvent) => {
            if (event.propertyName !== 'transform' || open) {
                return
            }
            setMounted(false)
        },
        [open],
    )

    if (!mounted || !bounds) {
        return null
    }

    return createPortal(
        <Paper
            elevation={3}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onTransitionEnd={handleTransitionEnd}
            sx={(theme) => ({
                position: 'fixed',
                top: bounds.top + 4,
                left: bounds.left - DRAWER_WIDTH,
                height: bounds.height - 8,
                width: DRAWER_WIDTH,
                zIndex: DRAWER_Z_INDEX,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                transform: revealed ? 'translateX(0)' : `translateX(${DRAWER_WIDTH}px)`,
                transition: theme.transitions.create('transform', {
                    duration: 240,
                    easing: theme.transitions.easing.easeInOut,
                }),
                backgroundColor: theme.palette.mode === 'dark' ? '#101010' : '#ffffff',
                backgroundImage: 'none',
                borderRight: 'none',
                // Same elevation shadow as dialogs / Quick Trade Paper; clip so no shadow on the seam with the trade window.
                clipPath: 'inset(-24px 0 -24px -24px)',
                ...(theme.palette.mode === 'dark'
                    ? {
                          borderTop: '1px solid #2b2e38',
                          borderBottom: '1px solid #2b2e38',
                          borderLeft: '1px solid #2b2e38',
                          boxSizing: 'border-box',
                      }
                    : {
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          borderLeft: `1px solid ${theme.palette.divider}`,
                      }),
            })}
        >
            <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                <ChargesBreakdownPanel
                    items={items}
                    total={total}
                    requiredMargin={requiredMargin}
                    availableMargin={availableMargin}
                    brokerCode={brokerCode}
                    getAmount={getAmount}
                />
            </Box>
        </Paper>,
        document.body,
    )
}

export default ChargesBreakdownDrawer
